import React from "react";
import { Field, useField, useFormikContext } from "formik";
import { FormField } from "@avamae/formbuilder";
import capitaliseStringFirstLetter from "helpers/capitaliseStringFirstLetter";

export type TAccessRights = {
  displayName: string;
  path: string;
  bDefault: boolean;
  bRequired: boolean;
  bHasAccess: boolean;
  childElements: TAccessRights[];
};

export interface AccessRightsProps {
  name: string;
  readOnly?: boolean;
}

export type AreaAccessLevel = "FullAccess" | "LimitedAccess" | "NoAccess";

type AccessRightsInnerProps = {
  accessRights: TAccessRights;
  onChange(v: TAccessRights): void;
  name: string;
  readOnly?: boolean;
};

const AccessRightsInner: React.FC<AccessRightsInnerProps> = ({
  accessRights,
  onChange,
  name,
  readOnly,
}) => {
  const handleAccessLevelChange = (
    accessLevel: "NoAccess" | "FullAccess" | "LimitedAccess"
  ) => {
    // const [, accessLevel] = e.target.id.split("_") as [string, AreaAccessLevel];
    if (accessLevel === "NoAccess") {
      onChange({
        ...accessRights,
        bHasAccess: false,
        childElements: accessRights.childElements?.map(c => ({
          ...c,
          bHasAccess: false,
        })),
      });
    } else if (accessLevel === "FullAccess") {
      onChange({
        ...accessRights,
        bHasAccess: true,
        childElements: accessRights.childElements?.map(c => ({
          ...c,
          bHasAccess: true,
        })),
      });
    } else {
      onChange({
        ...accessRights,
        bHasAccess: true,
        childElements: accessRights.childElements?.map(c => ({
          ...c,
          bHasAccess: false,
        })),
      });
    }
  };

  const handleAccessChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    name: string,
    formikProps: any,
    accessRights: TAccessRights
  ) => {
    if (e.target.checked) {
      formikProps.form.setFieldValue(name, {
        ...accessRights,
        bHasAccess: true,
      });
    } else {
      formikProps.form.setFieldValue(name, {
        ...accessRights,
        bHasAccess: false,
      });
    }
  };

  const getAreaAccessLevel = (accessRights: TAccessRights): AreaAccessLevel => {
    if (accessRights.bHasAccess) {
      let fullAccess = true;
      accessRights.childElements &&
        accessRights.childElements.forEach(a => {
          if (!a.bHasAccess) {
            fullAccess = false;
          }
        });
      return fullAccess ? "FullAccess" : "LimitedAccess";
    }

    return "NoAccess";
  };

  const getLimitedAccessDisabled = (accessRights: TAccessRights) => {
    if (accessRights.childElements) {
      return accessRights.childElements.length === 0;
    }
    return true;
  }

  return (
    <div
      className={`FormBox AccessRights ${
        readOnly ? "ReadOnly" : "FormSideBySide"
      }`}
      key={accessRights.path}
    >
      {readOnly ? (
        <div className="FormLabel NoPaddingTop">
          <ul>
            <li>
              <label>{accessRights.displayName}:</label>
            </li>
          </ul>
        </div>
      ) : (
        <div className="FormLabel NoPaddingTop">
          <label>{accessRights.displayName}</label>
        </div>
      )}

      <div className={"InputContainer"}>
        {readOnly ? (
          <div className="FormField">
            {getAreaAccessLevel(accessRights) === "FullAccess"
              ? "Full"
              : getAreaAccessLevel(accessRights) === "LimitedAccess"
              ? "Limited"
              : "None"}
          </div>
        ) : (
          <div className="RadioButtons">
            <div
              className="Radio"
              role="radio"
              aria-checked={getAreaAccessLevel(accessRights) === "FullAccess"}
              onClick={() => {
                handleAccessLevelChange("FullAccess");
              }}
            >
              <input
                id={accessRights.displayName + "_FullAccess"}
                type="radio"
                name={accessRights.displayName}
                value="FullAccess"
                disabled={readOnly}
                checked={getAreaAccessLevel(accessRights) === "FullAccess"}
              />
              <label
                className="CustomInput"
                htmlFor={accessRights.displayName + "_FullAccess"}
              >
                Full
              </label>
            </div>
            <div
              className={`Radio ${getLimitedAccessDisabled(accessRights) ? "disabled" : ""}`} // IXSTD-5280 For hyperlinks and modules without children, disable the LimitedAccess option
              role="radio"
              aria-checked={
                getAreaAccessLevel(accessRights) === "LimitedAccess"
              }
              aria-disabled={getLimitedAccessDisabled(accessRights)}
              onClick={() => {
                getLimitedAccessDisabled(accessRights)             
                ? null                                                                        // IXSTD-5280 For hyperlinks and modules without children, disable the LimitedAccess option     
                : handleAccessLevelChange("LimitedAccess") 
              }}
            >
              <input
                id={accessRights.displayName + "_LimitedAccess"}
                type="radio"
                name={accessRights.displayName}
                disabled={readOnly || getLimitedAccessDisabled(accessRights)}                 // IXSTD-5280 For hyperlinks and modules without children, disable the LimitedAccess option
                value="LimitedAccess"
                checked={getAreaAccessLevel(accessRights) === "LimitedAccess"}
              />
              <label
                className="CustomInput"
                htmlFor={accessRights.displayName + "_LimitedAccess"}
              >
                Limited
              </label>
            </div>
            <div
              className="Radio"
              role="radio"
              aria-checked={getAreaAccessLevel(accessRights) === "NoAccess"}
              onClick={() => {
                handleAccessLevelChange("NoAccess");
              }}
            >
              <input
                id={accessRights.displayName + "_NoAccess"}
                type="radio"
                name={accessRights.displayName}
                disabled={readOnly}
                value="NoAccess"
                checked={getAreaAccessLevel(accessRights) === "NoAccess"}
              />
              <label
                className="CustomInput"
                htmlFor={accessRights.displayName + "_NoAccess"}
              >
                None
              </label>
            </div>
          </div>
        )}
        {getAreaAccessLevel(accessRights) === "LimitedAccess" && (
          <div
            className={`MultiChoice AccessPermissionsInputContainer ${
              readOnly ? "ReadOnly" : ""
            }`}
          >
            {readOnly && `(`}
            {accessRights.childElements.map((c: any, i: number) => {
              const hasAccessIndexes = accessRights.childElements
                .map((item, i) => {
                  if (item.bHasAccess) {
                    return i;
                  } else {
                    return null;
                  }
                })
                .filter(item => item !== null);

              const hideComma =
                hasAccessIndexes &&
                hasAccessIndexes.length > 0 &&
                i === hasAccessIndexes[hasAccessIndexes.length - 1];

              let parsedName: string = "";

              if (c.path && accessRights.path) {
                parsedName = c.path.replace(`${accessRights.path}/`, "");

                parsedName = parsedName.replace(/-/g, " ").trim();

                if (!hideComma) {
                  parsedName = `${parsedName}, `;
                }

                parsedName = capitaliseStringFirstLetter(parsedName);
              }

              return (
                <React.Fragment key={i}>
                  {readOnly && c.bHasAccess && (
                    <div className="ReadOnlyItem">{parsedName}</div>
                    // <div className="ReadOnlyItem">{c.displayName}</div>
                  )}
                  {!readOnly && (
                    <Field name={`${name}.childElements[${i}]`}>
                      {(props: any) => (
                        <div className="Checkbox" key={c.path}>
                          <input
                            type="checkbox"
                            id={c.path}
                            value={c.displayName}
                            checked={c.bHasAccess}
                            disabled={readOnly}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) =>
                              handleAccessChange(
                                e,
                                `${name}.childElements[${i}]`,
                                props,
                                c
                              )
                            }
                          />
                          <label className="CustomInput" htmlFor={c.path}>
                            {c.displayName}
                          </label>
                        </div>
                      )}
                    </Field>
                  )}
                </React.Fragment>
              );
            })}
            {readOnly && `)`}
          </div>
        )}
      </div>
    </div>
  );
};

export interface AccessRightsSectionProps {
  name: string;
  readOnly?: boolean;
}

const AccessRightsSection: React.FC<AccessRightsSectionProps> = ({
  name,
  readOnly,
}) => {
  const [field] = useField<TAccessRights>(name);
  const formik = useFormikContext<TAccessRights>();

  return (
    <AccessRightsInner
      readOnly={readOnly}
      accessRights={field.value}
      onChange={(v: TAccessRights) =>
        formik.setFieldValue(name as keyof TAccessRights, v)
      }
      name={name}
      key={name}
    />
  );
};

export const AccessRights: React.FC<AccessRightsProps> = ({
  name,
  readOnly,
}) => {
  const [field] = useField<TAccessRights[]>(name);

  return (
    <>
      {Array(field.value.length)
        .fill(null)
        .map((_a, i) => (
          <AccessRightsSection
            readOnly={readOnly}
            name={`${field.name}[${i}]`}
            key={i}
          />
        ))}
    </>
  );
};

export const accessRightsRenderer = (readOnly: boolean) =>
  function accessRightsRenderer<T>(_key: React.ReactText, field: FormField<T>) {
    return (
      <>
        <AccessRights readOnly={readOnly} name={field.name as string} />
      </>
    );
  };
